import type { AnyObject } from 'antd/es/_util/type'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'

import { DATE_FORMAT_DAY_MONTH_YEAR_SHORT } from './date'
import { formatDisplayNumber } from './number'

type BaseConfig = {
  key: string
  label: ReactNode
  clearKeys?: string[]
  onClear?: () => void
}

export type ActiveFilterConfig =
  | ({ type: 'select' } & BaseConfig)
  | ({
      type: 'dateRange'
      from: string
      to: string
      dateFormat?: string
    } & BaseConfig)
  | ({ type: 'numberMin' | 'numberMax' } & BaseConfig)
  | ({ type: 'numberRange'; min: string; max: string } & BaseConfig)

export type ResolvedActiveFilterTag = {
  key: string
  label: ReactNode
  values: ReactNode[]
  clearKeys: string[]
  onClear?: () => void
}

type GetLabel = (key: string, value: string | number) => string | undefined

const hasValue = (value: unknown): value is string | number =>
  value !== undefined && value !== null && value !== ''

const toValues = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .flatMap((entry) => (entry == null ? [] : String(entry).split(',')))
    .map((entry) => entry.trim())
    .filter(Boolean)

const formatNumberRange = (min?: string | number, max?: string | number) => {
  if (hasValue(min) && hasValue(max)) {
    return `${formatDisplayNumber(min)} - ${formatDisplayNumber(max)}`
  }
  if (hasValue(min)) return `>= ${formatDisplayNumber(min)}`
  if (hasValue(max)) return `<= ${formatDisplayNumber(max)}`

  return undefined
}

export const resolveActiveFilterTag = (
  config: ActiveFilterConfig,
  params: AnyObject,
  getLabel?: GetLabel,
  locale?: string
): ResolvedActiveFilterTag | null => {
  let values: ReactNode[] = []
  let clearKeys = config.clearKeys ?? [config.key]

  switch (config.type) {
    case 'select':
      values = toValues(params?.[config.key]).map(
        (value) => getLabel?.(config.key, value) ?? value
      )
      break
    case 'dateRange': {
      const range = [params?.[config.from], params?.[config.to]].filter(
        Boolean
      ) as string[]

      if (range.length) {
        values = [
          range
            .map((date) => {
              const parsed = dayjs(date)

              return (locale ? parsed.locale(locale) : parsed).format(
                config.dateFormat ?? DATE_FORMAT_DAY_MONTH_YEAR_SHORT
              )
            })
            .join(' - ')
        ]
      }

      clearKeys = config.clearKeys ?? [config.from, config.to]
      break
    }
    case 'numberMin':
    case 'numberMax': {
      const value = params?.[config.key]
      const operator = config.type === 'numberMin' ? '>=' : '<='

      if (hasValue(value))
        values = [`${operator} ${formatDisplayNumber(value)}`]
      break
    }
    case 'numberRange': {
      const value = formatNumberRange(
        params?.[config.min],
        params?.[config.max]
      )

      if (value) values = [value]
      clearKeys = config.clearKeys ?? [config.min, config.max]
      break
    }
  }

  if (!values.length) return null

  return {
    key: config.key,
    label: config.label,
    values,
    clearKeys,
    onClear: config.onClear
  }
}

export const getActiveFilterTagsCount = (
  configs: ActiveFilterConfig[],
  params: AnyObject
): number =>
  configs.reduce(
    (count, config) => count + (resolveActiveFilterTag(config, params) ? 1 : 0),
    0
  )

export const getActiveFilterClearKeys = (
  configs: ActiveFilterConfig[]
): string[] =>
  configs.flatMap((config) => {
    if (config.clearKeys) return config.clearKeys

    switch (config.type) {
      case 'dateRange':
        return [config.from, config.to]
      case 'numberRange':
        return [config.min, config.max]
      default:
        return [config.key]
    }
  })
